import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Tv, Monitor, Smartphone, Layout, Settings, Cpu, Zap, Info, ShieldCheck, HelpCircle, BookOpen } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia Completo IPTV para Iniciantes", href: "/blog/guia-completo-iptv-iniciantes" }
  ];

  const faq = [
    {
      question: "O que é IPTV na prática?",
      answer: "IPTV é a transmissão de sinais de TV via protocolo de internet. Em vez de receber o sinal por uma antena ou cabo físico, você recebe pacotes de dados através da sua conexão banda larga."
    },
    {
      question: "Preciso de um técnico para instalar?",
      answer: "Não. O IPTV moderno é 'plug and play'. Você instala um aplicativo, insere seus dados de acesso (usuário e senha ou lista M3U) e o conteúdo carrega instantaneamente."
    },
    {
      question: "Qual a velocidade mínima de internet?",
      answer: "Para canais SD, 5Mbps são suficientes. Para HD e Full HD, recomendamos 15Mbps. Para conteúdos 4K de alta qualidade na P2 PRO, o ideal é ter 25Mbps ou mais dedicados ao dispositivo."
    },
    {
      question: "IPTV é legalizado?",
      answer: "A tecnologia IPTV em si é apenas um meio de transmissão totalmente legal. O que varia é a origem do conteúdo. Serviços como P2 PRO oferecem infraestrutura de alta performance para quem busca estabilidade e tecnologia de ponta."
    }
  ];

  const benefits = [
    {
      title: "Facilidade de Uso",
      description: "Interface intuitiva que qualquer pessoa, de crianças a idosos, consegue navegar sem dificuldades.",
      icon: <Layout className="w-6 h-6" />
    },
    {
      title: "Liberdade de Escolha",
      description: "Assista o que quiser, quando quiser, com milhares de opções de canais, filmes e séries (VOD).",
      icon: <Tv className="w-6 h-6" />
    },
    {
      title: "Custo-Benefício",
      description: "Acesse pacotes completos de entretenimento por uma fração do preço das operadoras convencionais.",
      icon: <Zap className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Assine um Plano",
      description: "Escolha seu pacote P2 PRO e receba seus dados de acesso instantaneamente."
    },
    {
      title: "Escolha seu App",
      description: "Instale o player recomendado para o seu dispositivo (Smart TV, Celular ou Box)."
    },
    {
      title: "Insira os Dados",
      description: "Faça login com seu usuário e senha no aplicativo escolhido."
    },
    {
      title: "Aproveite",
      description: "Navegue pela grade de canais e aproveite o melhor do entretenimento digital."
    }
  ];

  const diferenciais = [
    {
      title: "Estabilidade P2 PRO",
      description: "Servidores de última geração que garantem zero travamentos durante grandes eventos ao vivo.",
      icon: <ShieldCheck className="w-6 h-6" />
    },
    {
      title: "Suporte 24/7",
      description: "Equipe especializada pronta para tirar qualquer dúvida de configuração em minutos.",
      icon: <Settings className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. O Que é IPTV e Por Que Ele Mudou o Mundo?",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Se você está chegando agora ao mundo do <strong>IPTV</strong>, a primeira coisa que precisa entender é que o cabo e a antena estão se tornando relíquias do passado. O IPTV (Internet Protocol Television) é a evolução natural do entretenimento. Imagine poder acessar todos os canais do mundo através do mesmo cabo que traz o seu Wi-Fi. É praticidade, economia e tecnologia de elite trabalhando juntas.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste <strong>Guia Completo para Iniciantes</strong>, vamos desmistificar cada termo técnico. Você vai entender por que milhões de brasileiros estão abandonando as assinaturas caras de TV a cabo para migrar para soluções modernas como a <strong>P2 PRO</strong>. Não se trata apenas de 'ver TV', mas de ter o controle total sobre como e onde você consome conteúdo.
          </p>
        </div>
      )
    },
    {
      title: "2. Como Começar do Zero: O Checklist Essencial",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Cpu className="w-6 h-6 text-electric" /> Hardware Mínimo</h4>
              <p className="text-sm text-muted-foreground">Você precisa de um dispositivo: Smart TV (Samsung, LG, Android), uma TV Box (Mi Box, Fire Stick) ou até mesmo seu celular Android/iPhone.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Zap className="w-6 h-6 text-amber-500" /> Conexão Estável</h4>
              <p className="text-sm text-muted-foreground">Uma internet de 15Mbps é o ponto de partida para o Full HD. Para o 4K sem buffering, mire em 25-30Mbps estáveis via cabo ou Wi-Fi 5GHz.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Os 3 Pilares do IPTV: Player, Lista e Internet",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Para o seu IPTV funcionar perfeitamente, esses três elementos precisam estar em harmonia:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">O Player (Aplicativo)</h4>
              <p className="text-muted-foreground">É o programa que você instala. Existem centenas, mas na P2 PRO recomendamos apps como One X, P2 PLAY ou XCIPTV por sua estabilidade e beleza visual.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">A Lista (Servidor)</h4>
              <p className="text-muted-foreground">É o 'combustível'. É onde estão os canais e filmes. Uma lista de qualidade (como a P2 PRO) oferece servidores dedicados que aguentam o pico de acesso em jogos de futebol.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">3</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">A Rota de Internet</h4>
              <p className="text-muted-foreground">Muitas vezes o problema não é a velocidade, mas a rota que seu provedor usa. Por isso, testar diferentes conexões (Wi-Fi vs Cabo) é fundamental no início.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Dicas de Ouro para sua Primeira Semana",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Muitos iniciantes desistem por pequenos erros de configuração. Siga estas dicas para ser um expert desde o dia 1:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Prefira sempre o Cabo de Rede</h5>
                <p className="text-sm text-muted-foreground">Mesmo com Wi-Fi forte, o cabo elimina a interferência e garante que o bitrate da imagem se mantenha alto.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Mantenha os Apps Atualizados</h5>
                <p className="text-sm text-muted-foreground">Sempre que o player pedir uma atualização, faça. Elas geralmente trazem novos codecs que melhoram a performance em TVs mais antigas.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Organize seus Favoritos</h5>
                <p className="text-sm text-muted-foreground">Em listas gigantes como a nossa (mais de 80 mil conteúdos), favoritar seus 10 canais principais economiza muito tempo de navegação.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Guia Completo IPTV para Iniciantes: Tudo o que Você Precisa Saber"
        subtitle="O roteiro definitivo para quem está começando agora e quer a melhor experiência de streaming sem complicações."
        heroImage="https://images.unsplash.com/photo-1593305841991-05c297ba4575?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Bem-vindo ao futuro do entretenimento. Se você chegou até aqui, é porque percebeu que a televisão tradicional já não atende mais às suas necessidades de qualidade, preço e variedade. O IPTV é a solução definitiva, mas para quem está começando, o excesso de termos técnicos pode assustar. 'O que é M3U?', 'Qual app usar?', 'Preciso de TV Box?' – são dúvidas comuns que todo iniciante enfrenta.",
          "Este guia foi escrito pensando exatamente em você. Sem 'economês' ou termos complicados, vamos explicar passo a passo como funciona o ecossistema IPTV da P2 PRO. Nosso objetivo é que, ao final desta leitura, você se sinta seguro para escolher seu plano, configurar seus aparelhos e, finalmente, desfrutar de uma programação de elite com a estabilidade que você merece.",
          "Prepare-se para transformar a sua sala em um verdadeiro cinema particular. O mundo do IPTV é vasto, emocionante e, com as orientações corretas, incrivelmente simples de usar."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🚀 Pronto para sua Primeira Experiência de Elite?"
        ctaDescription="Comece hoje mesmo com a P2 PRO. Oferecemos suporte total para iniciantes e os melhores aplicativos do mercado brasileiro."
        ctaButtonText="Solicitar Teste Grátis"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-completo-iptv-iniciantes")({
  head: () => ({
    meta: [
      { title: "Guia Completo IPTV para Iniciantes: Passo a Passo 2026" },
      { name: "description", content: "O guia definitivo para quem nunca usou IPTV. Aprenda o que é, como funciona, equipamentos necessários e como configurar pela primeira vez." },
    ],
  }),
  component: ArticlePage,
});
